/**
 * Reading the grid's multi-select state for an export.
 *
 * The state is a map keyed by record _id, plus a reserved `__invertSelection` key
 * (see components/contextProviders/MultiSelectBoundary):
 *  - without `__invertSelection`, the ids in the map are the rows the user ticked;
 *  - with `__invertSelection`, the user chose "select all", and the ids in the map are the
 *    rows they unticked afterwards.
 *
 * So the same map means opposite things depending on that one key, and an export has to read
 * it accordingly or it sends the wrong records.
 */

const INVERT_KEY = '__invertSelection';

/**
 * The rows the user ticked. Empty when they used "select all" instead, or picked nothing.
 * @param {Record<string, any>} [selection] the grid's multi-select state
 * @returns {string[]} record ids
 */
export function getSelectedIds(selection) {
    if (!selection || selection[INVERT_KEY]) return [];
    return getMarkedIds(selection);
}

/**
 * The rows the user unticked after choosing "select all". Empty when they did not use
 * "select all", or unticked nothing afterwards.
 * @param {Record<string, any>} [selection] the grid's multi-select state
 * @returns {string[]} record ids
 */
export function getDeselectedIds(selection) {
    if (!selection?.[INVERT_KEY]) return [];
    return getMarkedIds(selection);
}

/**
 * Every id in the map, whatever it currently means.
 * @param {Record<string, any>} selection
 * @returns {string[]} record ids
 */
function getMarkedIds(selection) {
    return Object.keys(selection).filter(key => key !== INVERT_KEY && selection[key] === true);
}
